import i18n from '../../i18n/config';
import { useTranslation } from 'react-i18next';
// Análisis IA del ticket: qué pasó, causas, riesgo, acción + tareas y mensaje sugeridos.
// Desacoplado del centro de acción vía evento window 'aps-ticket-action'.
import React, { useEffect, useState, useCallback } from 'react';
import {
  Paper, Box, Typography, Stack, Chip, Button, IconButton, Tooltip, CircularProgress,
  Divider, TextField, Alert,
} from '@mui/material';
import {
  AutoAwesome as AIIcon, Refresh as RefreshIcon, ReportProblem as CauseIcon,
  Warning as RiskIcon, TaskAlt as ActionIcon, AddTask as AddTaskIcon, ContentCopy as CopyIcon,
  Chat as ChatIcon, WhatsApp as WhatsAppIcon, DirectionsCar as CarIcon, Tune as ProfileIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { useNotification } from '../common/GlobalNotifications';

export const emitTicketAction = (action, extra = {}) =>
  window.dispatchEvent(new CustomEvent('aps-ticket-action', { detail: { action, ...extra } }));

export default function TicketAIAnalysisCard({ ticket }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regen, setRegen] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async (force = false) => {
    force ? setRegen(true) : setLoading(true);
    try {
      const r = force ? await api.alarmTickets.regenTicketAiAnalysis(ticket.id) : await api.alarmTickets.ticketAiAnalysis(ticket.id);
      setData(r.data); setMsg(r.data?.suggested_message || '');
    } catch (e) { showError(t('hardcoded.components_tickets_ticketaianalysiscard.no_se_pudo_obtener_el')); }
    finally { setLoading(false); setRegen(false); }
  }, [ticket.id, showError]);

  useEffect(() => { load(false); }, [load]);

  const createSuggested = async (sug) => {
    try {
      await api.alarmTickets.createTicketTask(ticket.id, {
        title: sug.title, description: sug.description, priority: sug.priority, checklist: sug.checklist,
      });
      showSuccess(t('hardcoded.components_tickets_ticketactioncenter.tarea_creada'));
      window.dispatchEvent(new CustomEvent('aps-ticket-collab-changed'));
    } catch (e) { showError(e?.response?.data?.detail || t('hardcoded.components_tickets_ticketaianalysiscard.no_se_pudo_crear_la')); }
  };
  const copyMsg = () => { navigator.clipboard?.writeText(msg); showSuccess(t('hardcoded.components_tickets_ticketactioncenter.mensaje_copiado')); };

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <AIIcon color="primary" />
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>{t('hardcoded.components_tickets_ticketaianalysiscard.analisis_ia_del_ticket')}</Typography>
        {data?.generated_by === 'ai' && <Chip size="small" label="IA" color="primary" sx={{ height: 20 }} />}
        {data?.generated_by === 'rules' && <Chip size="small" label={t('hardcoded.components_tickets_ticketaianalysiscard.reglas')} variant="outlined" sx={{ height: 20 }} />}
        <Tooltip title={t('hardcoded.components_ai_airecommendationspanel.regenerar')}><span><IconButton size="small" onClick={() => load(true)} disabled={regen}>{regen ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}</IconButton></span></Tooltip>
      </Stack>

      {loading ? <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={22} /></Box> : data && (
        <Stack spacing={1.5}>
          {data.recurrence_note && <Alert severity="warning" sx={{ py: 0.5 }}>{data.recurrence_note}</Alert>}

          <Block icon={<ActionIcon fontSize="small" sx={{ color: '#0288d1' }} />} title={t('hardcoded.components_tickets_ticketaianalysiscard.que_paso')} text={data.summary} />
          {data.causes?.length > 0 && (
            <Box>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}><CauseIcon fontSize="small" sx={{ color: '#ed6c02' }} /><Typography variant="subtitle2" fontWeight={700}>{t('hardcoded.components_tickets_ticketaianalysiscard.posibles_causas')}</Typography></Stack>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>{data.causes.map((c, i) => <Typography key={i} component="li" variant="body2" sx={{ my: 0.25 }}>{c}</Typography>)}</Box>
            </Box>
          )}
          <Block icon={<RiskIcon fontSize="small" sx={{ color: '#d32f2f' }} />} title={t('hardcoded.components_tickets_ticketaianalysiscard.riesgo')} text={data.risk} />
          <Block icon={<ActionIcon fontSize="small" sx={{ color: '#2e7d32' }} />} title={t('hardcoded.components_tickets_ticketaianalysiscard.accion_recomendada')} text={data.action} strong />

          {/* Acciones rápidas */}
          <Divider textAlign="left"><Typography variant="caption" color="text.secondary">{t('hardcoded.components_tickets_ticketaianalysiscard.acciones_rapidas')}</Typography></Divider>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
            <Button size="small" variant="outlined" startIcon={<AddTaskIcon />} onClick={() => emitTicketAction('suggested')}>{t('hardcoded.components_tickets_ticketactioncenter.crear_tarea')}</Button>
            <Button size="small" variant="outlined" startIcon={<ChatIcon />} onClick={() => emitTicketAction('chat', { message: msg })}>{t('hardcoded.components_tickets_ticketaianalysiscard.abrir_chat')}</Button>
            <Button size="small" variant="outlined" startIcon={<WhatsAppIcon />} onClick={() => emitTicketAction('whatsapp')}>{t('hardcoded.components_tickets_ticketaianalysiscard.whatsapp')}</Button>
            {ticket.vehicle_id && <Button size="small" variant="text" startIcon={<CarIcon />} onClick={() => navigate(`/vehicles/schematics?vehicle=${ticket.vehicle_id}`)}>{t('hardcoded.components_tickets_ticketaianalysiscard.ver_vehiculo')}</Button>}
            {ticket.alarm_profile_name && <Button size="small" variant="text" startIcon={<ProfileIcon />} onClick={() => navigate('/alarm-profiles')}>{t('hardcoded.components_tickets_ticketaianalysiscard.ver_perfil')}</Button>}
            <Button size="small" variant="text" onClick={() => navigate('/reports/acciones')}>{t('hardcoded.components_common_sidebar.resumen_de_acciones')}</Button>
          </Stack>

          {/* Tareas sugeridas */}
          {data.suggested_tasks?.length > 0 && (
            <>
              <Divider textAlign="left"><Typography variant="caption" color="text.secondary">{t('hardcoded.components_tickets_ticketactioncenter.tareas_sugeridas')}</Typography></Divider>
              <Stack spacing={0.75}>
                {data.suggested_tasks.map((sug, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>{sug.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{sug.checklist?.length || 0} {t('hardcoded.components_tickets_ticketaianalysiscard.pasos')}</Typography>
                    </Box>
                    <Button size="small" startIcon={<AddTaskIcon />} onClick={() => createSuggested(sug)}>{t('audit.actions.CREATE')}</Button>
                  </Paper>
                ))}
              </Stack>
            </>
          )}

          {/* Mensaje sugerido */}
          <Divider textAlign="left"><Typography variant="caption" color="text.secondary">{t('hardcoded.components_tickets_ticketaianalysiscard.mensaje_para_responsables')}</Typography></Divider>
          <TextField multiline minRows={2} size="small" value={msg} onChange={(e) => setMsg(e.target.value)} />
          <Stack direction="row" spacing={0.75}>
            <Button size="small" startIcon={<CopyIcon />} onClick={copyMsg}>{t('hardcoded.components_tickets_ticketactioncenter.copiar')}</Button>
            <Button size="small" startIcon={<ChatIcon />} onClick={() => emitTicketAction('chat', { message: msg })}>{t('hardcoded.components_tickets_ticketaianalysiscard.enviar_al_chat')}</Button>
            <Button size="small" startIcon={<WhatsAppIcon />} onClick={() => emitTicketAction('whatsapp', { message: msg })}>{t('hardcoded.components_tickets_ticketaianalysiscard.usar_en_whatsapp')}</Button>
          </Stack>
        </Stack>
      )}
    </Paper>
  );
}

function Block({ icon, title, text, strong }) {
  const { t } = useTranslation();
  if (!text) return null;
  return (
    <Box>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }}>{icon}<Typography variant="subtitle2" fontWeight={700}>{title}</Typography></Stack>
      <Typography variant="body2" sx={{ fontWeight: strong ? 600 : 400 }}>{text}</Typography>
    </Box>
  );
}
